import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Loader2, Check, Pencil, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import OpenOpnieuw from "./OpenOpnieuw";
import { useTaal } from "@/contexts/TaalContext";

/** Een bestaande taak in TASQ wijzigen. Post 10c, stap 3 en 4.
 *
 *  Dezelfde vorm als `TaakVoorstel`, en dezelfde regel van CONSTRAINTS 0.11:
 *  bevestiging op glas MET DE EXACTE WIJZIGING ERVOOR.
 *
 *    KIEZEN      Meer dan een open taak past. Er is NIETS opgeslagen; de
 *                kandidaten staan er als klikbare keuzes.
 *    NALEZEN     De taak, met de HUIDIGE en de NIEUWE waarde naast elkaar.
 *    GEWIJZIGD   TASQ heeft de wijziging doorgevoerd; link en bronregel.
 *
 *  DE VELDEN KOMEN VAN DE SERVER EN NIET UIT HET BERICHT. Het blok draagt
 *  alleen een id; de kaart haalt de rij op, en Bevestig stuurt precies die rij
 *  terug. Wat een taalmodel ook schrijft, het komt niet op deze knop.
 */
export default function TaakWijziging({ wijzigingId, keuze, zin, berichtId, onGekozen, onWijzig }) {
  const { t } = useTaal();
  const [rij, setRij] = useState(null);
  const [kandidaten, setKandidaten] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  // WF-40 (post a): het token van deze kaart, zolang er iets te bevestigen is.
  const { token, vernieuw, vervallen, laatVervallen, reden } = useKaartToken(
    rij?.id ? `${API}/taken/wijziging/${rij.id}/toon` : "", Boolean(rij?.id && !rij.uitgevoerd));

  useEffect(() => {
    let levend = true;
    const haal = async () => {
      try {
        if (keuze) {
          const r = await axios.post(`${API}/taken/wijziging`, { zin });
          if (!levend) return;
          if (r.data.keuze_nodig) setKandidaten(r.data.kandidaten || []);
          else if (!r.data.geen_treffer) setRij(r.data);
          else setFout("Er past geen open taak meer bij deze zin.");
        } else {
          const r = await axios.get(`${API}/taken/wijziging/${wijzigingId}`);
          if (levend) setRij(r.data);
        }
      } catch (e) {
        if (levend) setFout(e.response?.data?.detail || t("De wijziging is niet op te halen"));
      }
    };
    haal();
    return () => { levend = false; };
  }, [wijzigingId, keuze, zin, t]);

  // FIX 4: een verse kaart, dan pas een nieuw token.
  const openOpnieuw = async () => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.get(`${API}/taken/wijziging/${rij.id}`);
      setRij(r.data);
      if (!r.data.uitgevoerd) await vernieuw();
    } catch (e) {
      setFout(e.response?.data?.detail || t("De wijziging is niet op te halen"));
    } finally {
      setBezig(false);
    }
  };

  // EEN KLIK IS DE KEUZE: de taak gaat als eigen veld mee, langs de zinlezing
  // heen, net als bij de werkruimtekeuze (post 10b-fix punt 1).
  const kies = async (taakId) => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/taken/wijziging`, {
        zin, taak_id: taakId, ...(berichtId ? { bericht_id: berichtId } : {}),
      });
      if (r.data.keuze_nodig) setKandidaten(r.data.kandidaten || []);
      else if (r.data.geen_treffer) setFout("Die taak staat niet meer open.");
      else {
        setRij(r.data);
        if (onGekozen && r.data.bericht_tekst) onGekozen(r.data.bericht_tekst);
      }
    } catch (e) {
      setFout(e.response?.data?.detail || t("De wijziging is niet op te halen"));
    } finally {
      setBezig(false);
    }
  };

  const bevestig = async () => {
    setBezig(true);
    setFout("");
    try {
      // PRECIES WAT ER OP GLAS STAAT, en niets anders. De server vergelijkt dit
      // teken voor teken met de opgeslagen rij.
      const r = await axios.post(`${API}/taken/wijziging/${rij.id}/uitvoeren`, {
        taak_id: rij.taak_id,
        titel: rij.titel,
        werkruimte_id: rij.werkruimte_id,
        nieuwe_status: rij.nieuwe_status,
        nieuwe_deadline_iso: rij.nieuwe_deadline_iso,
        token,
      });
      setRij(r.data);
      toast.success(t("Taak gewijzigd in TASQ"));
    } catch (e) {
      const tekst = e.response?.data?.detail || t("De wijziging is niet doorgevoerd");
      setFout(tekst);
      // FIX 4: geen stil nieuw token; alleen "Open opnieuw". Punt 5: de reden blijft bewaard.
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  const wijzig = () => onWijzig && onWijzig(rij?.bronzin || zin || "");

  if (fout && !rij && !kandidaten) {
    return (
      <div className="border rounded-lg p-3" data-testid="wijziging-fout">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">{fout}</p>
      </div>
    );
  }

  if (keuze && !rij) {
    return (
      <div className="border rounded-lg p-3 space-y-2" data-testid="wijziging-keuze">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          {t("Er is nog niets gewijzigd. Kies de taak.")}
        </p>
        {kandidaten === null ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ul className="space-y-1">
            {kandidaten.map((k) => (
              <li key={k.id}>
                {/* MINSTENS 44 PUNTEN HOOG (CONSTRAINTS 0.3b). */}
                <button
                  type="button"
                  className="w-full text-left text-sm px-3 py-3 min-h-11 rounded border hover:bg-[hsl(var(--muted))]"
                  data-testid="wijziging-keuze-knop"
                  disabled={bezig}
                  onClick={() => kies(k.id)}
                >
                  <span className="font-medium">{k.titel}</span>
                  <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                    {k.werkruimte_naam} · {k.status_op_glas}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {fout && <p className="text-xs text-[hsl(var(--destructive))]">{fout}</p>}
      </div>
    );
  }

  if (!rij) {
    return <Loader2 className="h-4 w-4 animate-spin" />;
  }

  if (rij.uitgevoerd) {
    return (
      <div className="border rounded-lg p-3 space-y-1" data-testid="wijziging-gewijzigd">
        <p className="text-xs">
          <Check className="h-3 w-3 mr-1 inline" />
          {t("Gewijzigd in TASQ, werkruimte {naam}.", { naam: rij.werkruimte_naam })}
        </p>
        {rij.resultaat_status_op_glas && (
          <p className="text-xs" data-testid="wijziging-resultaat-status">{t("Status: {waarde}", { waarde: rij.resultaat_status_op_glas })}</p>
        )}
        {rij.resultaat_deadline_op_glas && (
          <p className="text-xs" data-testid="wijziging-resultaat-deadline">{t("Deadline: {waarde}", { waarde: rij.resultaat_deadline_op_glas })}</p>
        )}
        <a href={rij.verwijzing} target="_blank" rel="noreferrer"
           className="text-xs underline text-[hsl(var(--accent))] inline-flex items-center gap-1 min-h-11 py-3"
           data-testid="wijziging-link">
          {rij.titel}
          <ExternalLink className="h-3 w-3" />
        </a>
        <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="wijziging-bron">{rij.bron}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-3" data-testid="wijziging-nalezen">
      <dl className="text-xs space-y-1">
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Taak")}</dt>
          <dd className="min-w-0 font-medium" data-testid="wijziging-titel">{rij.titel}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Werkruimte")}</dt>
          <dd className="min-w-0" data-testid="wijziging-werkruimte">{rij.werkruimte_naam}</dd>
        </div>
        {rij.nieuwe_status && (
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Status")}</dt>
            <dd className="min-w-0" data-testid="wijziging-status">
              {rij.huidige_status_op_glas}
              <ArrowRight className="h-3 w-3 mx-1 inline" />
              <span className="font-medium">{rij.nieuwe_status_op_glas}</span>
            </dd>
          </div>
        )}
        {rij.nieuwe_deadline_iso && (
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Deadline")}</dt>
            <dd className="min-w-0" data-testid="wijziging-deadline">
              {rij.huidige_deadline_op_glas}
              <ArrowRight className="h-3 w-3 mx-1 inline" />
              <span className="font-medium">{rij.nieuwe_deadline_op_glas}</span>
            </dd>
          </div>
        )}
      </dl>
      {(fout || reden) && (
        <p className="text-xs text-[hsl(var(--destructive))]" data-testid="wijziging-foutmelding">{fout || reden}</p>
      )}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className={`min-h-11 px-4 ${vervallen ? GRIJS : ""}`} onClick={bevestig} disabled={bezig || !token}
                data-testid="wijziging-bevestig-btn">
          {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                 : <Check className="h-3 w-3 mr-1" />}
          {t("Bevestig")}
        </Button>
        {vervallen && <OpenOpnieuw onClick={openOpnieuw} bezig={bezig} />}
        <Button size="sm" variant="outline" className="min-h-11 px-4" disabled={bezig}
                onClick={wijzig} data-testid="wijziging-wijzig-btn">
          <Pencil className="h-3 w-3 mr-1" />
          {t("Wijzig")}
        </Button>
      </div>
    </div>
  );
}
